import React from 'react';
import { Sparkles, Zap, Crown, ArrowRight } from 'lucide-react';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';

interface FeatureLockProps {
  feature: 'canExportPDF' | 'canUseWebhooks' | 'canViewStaffMetrics' | 'canViewRevenueLeak' | 'canViewFinancials' | 'canCustomizeAlerts' | 'canAccessAPI' | 'hasMultiLocation' | 'hasTeamAccounts';
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onUpgradeClick?: () => void;
}

// Feature metadata with enticing descriptions and icons
const FEATURE_META: Record<string, { name: string; benefit: string; icon: 'sparkles' | 'zap' | 'crown' }> = {
  canExportPDF: { name: 'Export Reports', benefit: 'Download & share', icon: 'sparkles' },
  canUseWebhooks: { name: 'Webhooks', benefit: 'Automate your workflow', icon: 'zap' },
  canViewStaffMetrics: { name: 'Staff Insights', benefit: 'Track performance', icon: 'sparkles' },
  canViewRevenueLeak: { name: 'Revenue Protection', benefit: 'Stop losing money', icon: 'zap' },
  canViewFinancials: { name: 'Financial Analytics', benefit: 'Deep insights', icon: 'crown' },
  canCustomizeAlerts: { name: 'Custom Alerts', benefit: 'Your rules, your way', icon: 'sparkles' },
  canAccessAPI: { name: 'API Access', benefit: 'Build integrations', icon: 'zap' },
  hasMultiLocation: { name: 'Multi-Location', benefit: 'Manage all sites', icon: 'crown' },
  hasTeamAccounts: { name: 'Team Access', benefit: 'Collaborate together', icon: 'crown' },
};

const IconMap = {
  sparkles: Sparkles,
  zap: Zap,
  crown: Crown,
};

export function FeatureLock({ feature, children, fallback, onUpgradeClick }: FeatureLockProps) {
  const { isFeatureLocked } = usePlanFeatures();

  if (!isFeatureLocked(feature)) {
    return <>{children}</>;
  }

  const meta = FEATURE_META[feature] || { name: 'Premium Feature', benefit: 'Unlock more power', icon: 'sparkles' };
  const Icon = IconMap[meta.icon];

  if (fallback) {
    return <>{fallback}</>;
  }

  // Beautiful, enticing upgrade button with gradient and animation
  return (
    <button
      onClick={onUpgradeClick}
      className="group relative flex items-center gap-2 px-4 py-2.5 
                 bg-gradient-to-r from-violet-600/20 via-purple-600/20 to-fuchsia-600/20
                 border border-purple-500/30 rounded-xl
                 hover:from-violet-600/30 hover:via-purple-600/30 hover:to-fuchsia-600/30
                 hover:border-purple-400/50 hover:shadow-lg hover:shadow-purple-500/20
                 transition-all duration-300 cursor-pointer overflow-hidden"
    >
      {/* Animated shimmer effect */}
      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      {/* Icon with glow */}
      <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-fuchsia-500 shadow-lg shadow-purple-500/30">
        <Icon className="w-4 h-4 text-white" />
      </div>
      
      {/* Text content */}
      <div className="relative text-left hidden sm:block">
        <p className="text-sm font-medium text-white/90">{meta.name}</p>
        <p className="text-xs text-purple-300/70">{meta.benefit}</p>
      </div>
      
      {/* Arrow indicator */}
      <ArrowRight className="relative w-4 h-4 text-purple-400 group-hover:translate-x-1 transition-transform hidden sm:block" />
      
      {/* Plan badge */}
      <span className="relative ml-auto px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider 
                      bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full
                      shadow-lg shadow-amber-500/30 sm:hidden">
        Pro
      </span>
    </button>
  );
}

// Premium card wrapper for larger content areas
export function PremiumFeatureCard({ 
  feature, 
  children,
  onUpgradeClick,
}: { 
  feature: FeatureLockProps['feature']; 
  children: React.ReactNode;
  onUpgradeClick?: () => void;
}) {
  const { isFeatureLocked, getRequiredPlan } = usePlanFeatures();

  if (!isFeatureLocked(feature)) {
    return <>{children}</>;
  }

  const requiredPlan = getRequiredPlan(feature);
  const meta = FEATURE_META[feature] || { name: 'Premium Feature', benefit: 'Unlock more power', icon: 'sparkles' };
  const Icon = IconMap[meta.icon];

  return (
    <div className="relative group">
      {/* Blurred preview of content */}
      <div className="blur-[2px] opacity-40 pointer-events-none select-none">
        {children}
      </div>
      
      {/* Beautiful overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-900/80 via-purple-900/20 to-slate-900/80 backdrop-blur-[1px] rounded-xl">
        <div className="text-center p-6 max-w-xs">
          {/* Glowing icon */}
          <div className="relative mx-auto w-16 h-16 mb-4">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-fuchsia-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity" />
            <div className="relative w-full h-full bg-gradient-to-br from-purple-500 to-fuchsia-500 rounded-2xl flex items-center justify-center shadow-2xl">
              <Icon className="w-8 h-8 text-white" />
            </div>
          </div>
          
          {/* Title and description */}
          <h3 className="text-lg font-bold text-white mb-1">{meta.name}</h3>
          <p className="text-sm text-slate-300 mb-4">{meta.benefit}</p>
          
          {/* CTA Button */}
          <button
            onClick={onUpgradeClick}
            className="inline-flex items-center gap-2 px-5 py-2.5 
                     bg-gradient-to-r from-purple-600 to-fuchsia-600 
                     hover:from-purple-500 hover:to-fuchsia-500
                     text-white font-medium rounded-xl
                     shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50
                     transform hover:scale-105 transition-all duration-200"
          >
            <Crown className="w-4 h-4" />
            Upgrade to {requiredPlan === 'enterprise' ? 'Enterprise' : 'Pro'}
          </button>
          
          {/* Price hint */}
          <p className="mt-3 text-xs text-slate-400">
            Starting at <span className="text-purple-400 font-semibold">$29.99/mo</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// Simple wrapper for hiding content completely
export function PlanGate({ 
  feature, 
  children,
  showNothing = false,
  onUpgradeClick,
}: { 
  feature: FeatureLockProps['feature']; 
  children: React.ReactNode;
  showNothing?: boolean;
  onUpgradeClick?: () => void;
}) {
  const { isFeatureLocked, getRequiredPlan } = usePlanFeatures();

  if (isFeatureLocked(feature)) {
    if (showNothing) return null;
    
    const requiredPlan = getRequiredPlan(feature);
    const meta = FEATURE_META[feature] || { name: 'Premium Feature', benefit: 'Unlock more power', icon: 'sparkles' };
    const Icon = IconMap[meta.icon];
    
    return (
      <div 
        onClick={onUpgradeClick}
        className="group p-5 bg-gradient-to-br from-slate-800/50 via-purple-900/10 to-slate-800/50 
                   rounded-xl border border-purple-500/20 hover:border-purple-500/40
                   cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-fuchsia-500/20 
                         flex items-center justify-center group-hover:scale-110 transition-transform">
            <Icon className="w-6 h-6 text-purple-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium">{meta.name}</h4>
            <p className="text-sm text-slate-400">{meta.benefit}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs font-medium bg-gradient-to-r from-amber-500/20 to-orange-500/20 
                           text-amber-400 rounded-lg border border-amber-500/30">
              {requiredPlan === 'enterprise' ? 'Enterprise' : 'Pro'}
            </span>
            <ArrowRight className="w-5 h-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
